import { computed, reactive, watch } from "vue";
import { useUser } from "@/stores";

export function usePaymentSettings() {
  const userStore = useUser();

  const isCardPaymentEnabled = computed(() => {
    if (userStore.isDataLoaded) {
      return (
        userStore.settings.stripeConnected ||
        userStore.settings.qbConnected ||
        userStore.settings.squareConnected
      );
    }
    return false;
  });

  const isCardPaymentAccepted = computed(() => {
    if (userStore.isDataLoaded) {
      return userStore.settings.acceptCardPayments;
    }
    return false;
  });

  const effectiveSettings = reactive({ ...userStore.settings });

  watch(
    () => userStore.settings,
    (newSettings) => {
      Object.keys(newSettings).forEach((key) => {
        if (!isDisabled({ key })) {
          effectiveSettings[key] = newSettings[key];
        }
      });
    },
    { deep: true }
  );

  watch(isCardPaymentEnabled, (enabled) => {
    if (!enabled) {
      effectiveSettings.acceptCardPayments = false;
      effectiveSettings.acceptStripePayments = false;
      effectiveSettings.acceptSquarePayments = false;
      effectiveSettings.acceptQbPayments = false;
    } else {
      effectiveSettings.acceptCardPayments =
        userStore.settings.acceptCardPayments;
      effectiveSettings.acceptStripePayments =
        userStore.settings.acceptStripePayments;
      effectiveSettings.acceptSquarePayments =
        userStore.settings.acceptSquarePayments;
      effectiveSettings.acceptQbPayments = userStore.settings.acceptQbPayments;
    }
  });

  watch(isCardPaymentAccepted, (enabled) => {
    if (!enabled) {
      userStore.settings.acceptStripePayments = false;
      userStore.settings.acceptSquarePayments = false;
      userStore.settings.acceptQbPayments = false;
    }
  });

  const isDisabled = (row) => {
    switch (row.key) {
      case "acceptCardPayments":
        return !(
          userStore.settings.stripeConnected ||
          userStore.settings.qbConnected ||
          userStore.settings.squareConnected
        );
      case "acceptStripePayments":
        return !(
          userStore.settings.stripeConnected &&
          userStore.settings.acceptCardPayments &&
          !userStore.settings.acceptQbPayments &&
          !userStore.settings.acceptSquarePayments
        );
      case "acceptQbPayments":
        return !(
          userStore.settings.qbConnected &&
          userStore.settings.acceptCardPayments &&
          !userStore.settings.acceptStripePayments &&
          !userStore.settings.acceptSquarePayments
        );
      case "acceptSquarePayments":
        return !(
          userStore.settings.squareConnected &&
          userStore.settings.acceptCardPayments &&
          !userStore.settings.acceptQbPayments &&
          !userStore.settings.acceptStripePayments
        );
      default:
        return false;
    }
  };

  const showTooltip = (row) => {
    switch (row.key) {
      case "acceptCardPayments":
        return !isCardPaymentEnabled.value;
      case "acceptStripePayments":
        return (
          !userStore.settings.stripeConnected ||
          !userStore.settings.acceptCardPayments ||
          userStore.settings.acceptQbPayments ||
          userStore.settings.acceptSquarePayments
        );
      case "acceptQbPayments":
        return (
          !userStore.settings.qbConnected ||
          !userStore.settings.acceptCardPayments ||
          userStore.settings.acceptSquarePayments ||
          userStore.settings.acceptStripePayments
        );
      case "acceptSquarePayments":
        return (
          !userStore.settings.squareConnected ||
          !userStore.settings.acceptCardPayments
        );
      default:
        return false;
    }
  };

  const tooltipContent = (row) => {
    if (row.key === "acceptCardPayments") {
      return "Card payments are disabled. Make sure a card payment method is connected in Payment Settings.";
    } else if (row.key === "acceptStripePayments") {
      if (!userStore.settings.stripeConnected)
        return "Stripe payments are coming soon, stay tuned.";
      if (!userStore.settings.acceptCardPayments)
        return "Stripe payments are coming soon, stay tuned.";
      if (
        userStore.settings.acceptQbPayments ||
        userStore.settings.acceptSquarePayments
      )
        return "Stripe payments are coming soon, stay tuned.";
    } else if (row.key === "acceptSquarePayments") {
      if (!userStore.settings.squareConnected)
        return "Square payments are coming soon, stay tuned.";
      if (
        userStore.settings.acceptQbPayments ||
        userStore.settings.acceptStripePayments
      )
        return "Only one payment method can be enabled at a time.";
    } else if (row.key === "acceptQbPayments") {
      if (!userStore.settings.qbConnected)
        return "QuickBooks payments are disabled. Make sure a QuickBooks account is connected in Payment Settings.";
      if (userStore.settings.qbConnected) {
        if (!userStore.settings.acceptCardPayments)
          return "'Accept Card Payments' must be enabled to accept QuickBooks payments.";
        if (userStore.settings.acceptCardPayments)
          return "Enable this to accept payments through QuickBooks.";
      }
      if (
        userStore.settings.acceptStripePayments ||
        userStore.settings.acceptSquarePayments
      ) {
        return "Only one payment method can be enabled at a time.";
      }
    }
    return "";
  };

  return {
    isCardPaymentEnabled,
    isCardPaymentAccepted,
    effectiveSettings,
    isDisabled,
    showTooltip,
    tooltipContent,
  };
}
